/* eslint-disable */
import { useEffect, useState } from 'react';
import { Alert, Form, notification, Spin } from 'antd';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import { OverViewCardForm } from '../OverviewCardForm';
import { QueryObserverResult, RefetchOptions, useMutation } from '@tanstack/react-query';
import { LoadingOutlined } from '@ant-design/icons';
import { closeModal } from '@/store/modal/slice';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { User } from '@/types/TAccounts';
import { updateUser } from '@/api/user';
import CopyableText from '@/components/LabelWithdescription/CopyableText';
import TextEditor from '@/components/TextEditor/TextEditor';
import TextArea from 'antd/es/input/TextArea';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getAllVariables } from '@/store/novi/variables/selector';
import { setVariable } from '@/store/novi/variables/slice';
import { updateCampaignVariables } from '@/api/novi/CamapaignPortalApis';
import { getSelectedAgent } from '@/store/account/selector';
import { Variable } from '@/types/TVariables';
import { isValidSampleQA, safeJSONParse } from '@/utils/helper';
import { getSelectedAgentId } from '@/store/account/selector';

interface QuestionAndAnswerFormProps {
	sampleQA?: string;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	isNoviUser?: boolean;
}

export const QuestionAndAnswerForm: React.FC<QuestionAndAnswerFormProps> = ({
	sampleQA,
	refetch,
	isNoviUser,
}) => {
	const [form] = Form.useForm<{ sampleQA: string }>();
	const [isFieldTouchReset, setIsFieldTouchReset] = useState<boolean>(true);
	const dispatch = useAppDispatch();
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const variables = useAppSelector(getAllVariables);
	let selectedCampaign: any;

	if (isNoviUser) {
		selectedCampaign = useAppSelector(getSelectedAgent);
	}

	useEffect(() => {
		if (selectedCampaign?.variables?.length) {
			try {
				const parsedVariables = safeJSONParse(selectedCampaign.variables, {});
				Object.entries(parsedVariables).forEach(([key, value]) => {
					dispatch(setVariable({ name: key, data: value as Variable }));
				});
			} catch (error) {
				console.error('Error setting variables:', error);
			}
		}
	}, [selectedCampaign, dispatch]);

	const mutation = useMutation({
		mutationKey: ['update-client'],
		mutationFn: updateUser,
		onSuccess: () => {
			refetch();
			notification.success({
				key: 'form-success',
				message: 'Q&A has been saved successfully!',
				duration: 3,
			});
			setIsFieldTouchReset(false);
			dispatch(closeModal());
		},
	});

	const { mutate: updateVariables } = useMutation({
		mutationKey: ['update-variables'],
		mutationFn: () => updateCampaignVariables(selectedCampaign?.number, variables, 'Client'),
		onSuccess: () => {
			refetch();
			notification.success({
				key: 'form-success',
				message: 'Variables have been saved successfully!',
				duration: 3,
			});
			setIsFieldTouchReset(false);
		},
	});

	const handleVariableChange = (updatedVariables: any) => {
		Object.entries(updatedVariables).forEach(([key, value]) => {
			dispatch(setVariable({ name: key, data: value as Variable }));
		});

		updateVariables();
	};

	const handleSubmit = (value: any) => {
		if (!isValidSampleQA(value.sampleQA)) {
			notification.error({
				key: 'form-success',
				message: 'Q&A should be in provided format.',
				duration: 3,
			});
			return;
		}
		mutation.mutate({
			sample_qa: value.sampleQA,
			agentId: selectedAgentId as string,
		});
	};

	const FormItems = () => (
		<>
			<Form.Item
				label={
					<LabelWithDescription
						customClassName="mt-2 w-full"
						label={
							<div className="flex items-center gap-1">
								Questions and Answers (Q&A)
								<CopyableText text={sampleQA || ''} hideText />
							</div>
						}
					/>
				}
				name="sampleQA"
				className="!mb-0"
			>
				{isNoviUser ? (
					<TextEditor
						placeholder="Please enter your Q&A"
						variables={variables}
						onChange={(_, variables) => {
							if (variables) {
								handleVariableChange(variables);
							}
						}}
					/>
				) : (
					<TextArea rows={12} placeholder="Please enter your Q&A" />
				)}
			</Form.Item>
			<Alert
				className="mt-4"
				type="info"
				closable={false}
				message={
					<>
						<b>Please Note:</b>
						<p className="text-justify">
							Please write your questions and answers in separate paragraphs using the following
							format:
						</p>
						<span className="flex flex-col">
							<span>Question: Where are you located?</span>
							<span>Answer: We are located at 132 Linkin Park, New York.</span>
						</span>
						<br />
						<span className="flex flex-col">
							<span>Question: How many other branches do you have?</span>
							<span>Answer: We don't have any subsidiary branches.</span>
						</span>
					</>
				}
			/>
		</>
	);

	return (
		<Spin
			spinning={mutation.isPending}
			indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
		>
			<OverViewCardForm
				form={form}
				initialValues={{ sampleQA }}
				formItems={<FormItems />}
				handleSubmit={handleSubmit}
				isFieldTouchReset={isFieldTouchReset}
			/>
		</Spin>
	);
};
